import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X, Send, Loader2 } from 'lucide-react';
import './AiAssistantBubble.css';

import { API as API_URL, BACKEND_URL } from '../utils/apiConfig';
const AVATAR_URL = 'https://static.prod-images.emergentagent.com/jobs/7ef4282b-5af3-4d91-ba62-e7065e694d64/images/037513f8c49d58cc244b64ebf4872987e6ecbf21192f09db11a25490acfc04f2.png';

export const AiAssistantBubble = ({ rideId, rideSummary }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load chat history when opening
  useEffect(() => {
    if (isOpen && !historyLoaded && rideId) {
      loadHistory();
    }
  }, [isOpen, rideId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/ai-assistant/history/${rideId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.messages?.length > 0) {
        setMessages(res.data.messages);
        setSessionId(res.data.session_id);
      }
      setHistoryLoaded(true);
    } catch (e) {
      setHistoryLoaded(true);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMsg = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/ai-assistant/chat`,
        { message: userMsg.content, ride_id: rideId, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I could not process your request. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    { label: 'Price breakdown', msg: 'How is the price calculated for this ride?' },
    { label: 'Cancellation policy', msg: 'What is the cancellation policy?' },
    { label: 'Driver info', msg: 'Tell me about the driver and vehicle.' },
    { label: 'Travel tips', msg: 'Any travel tips for this route?' },
  ];

  return (
    <>
      {/* Floating bubble button */}
      {!isOpen && (
        <button
          data-testid="ai-assistant-bubble"
          onClick={() => setIsOpen(true)}
          className="ai-bubble-btn"
          aria-label="AI Assistant"
        >
          <img src={AVATAR_URL} alt="AI" className="ai-bubble-avatar" />
          <span className="ai-bubble-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="ai-chat-panel" data-testid="ai-chat-panel">
          {/* Header */}
          <div className="ai-chat-header">
            <div className="ai-chat-header-info">
              <img src={AVATAR_URL} alt="AI" className="ai-header-avatar" />
              <div>
                <h4 className="ai-header-title">VoiceRide AI</h4>
                <p className="ai-header-subtitle">
                  {rideSummary || 'Ride Concierge'}
                </p>
              </div>
            </div>
            <button
              data-testid="ai-chat-close"
              onClick={() => setIsOpen(false)}
              className="ai-chat-close-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="ai-chat-messages">
            {messages.length === 0 && !loading && (
              <div className="ai-welcome">
                <img src={AVATAR_URL} alt="AI" className="ai-welcome-avatar" />
                <p className="ai-welcome-text">
                  Hi! I'm your VoiceRide AI Concierge. Ask me anything about this ride, pricing, or travel tips!
                </p>
                <div className="ai-quick-questions">
                  {quickQuestions.map((q, i) => (
                    <button
                      key={i}
                      data-testid={`quick-q-${i}`}
                      onClick={() => { setInput(q.msg); setTimeout(sendMessage, 0); setInput(''); 
                        // Direct send
                        const userMsg = { role: 'user', content: q.msg };
                        setMessages(prev => [...prev, userMsg]);
                        setLoading(true);
                        const token = localStorage.getItem('token');
                        axios.post(`${API_URL}/ai-assistant/chat`, 
                          { message: q.msg, ride_id: rideId, session_id: sessionId },
                          { headers: { Authorization: `Bearer ${token}` } }
                        ).then(res => {
                          setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
                          if (res.data.session_id) setSessionId(res.data.session_id);
                        }).catch(() => {
                          setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I could not process your request.' }]);
                        }).finally(() => setLoading(false));
                      }}
                      className="ai-quick-btn"
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`ai-msg ${msg.role === 'user' ? 'ai-msg-user' : 'ai-msg-assistant'}`}
              >
                {msg.role === 'assistant' && (
                  <img src={AVATAR_URL} alt="AI" className="ai-msg-avatar" />
                )}
                <div className={`ai-msg-bubble ${msg.role === 'user' ? 'ai-msg-bubble-user' : 'ai-msg-bubble-ai'}`}>
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="ai-msg ai-msg-assistant">
                <img src={AVATAR_URL} alt="AI" className="ai-msg-avatar" />
                <div className="ai-msg-bubble ai-msg-bubble-ai ai-typing">
                  <Loader2 size={16} className="animate-spin" />
                  <span>Thinking...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="ai-chat-input-area">
            <input
              ref={inputRef}
              data-testid="ai-chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this ride..."
              className="ai-chat-input"
              disabled={loading}
            />
            <button
              data-testid="ai-chat-send"
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="ai-chat-send-btn"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
